import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { resetSingleAppointment } from '../../store/appointments/slice';
import { resetSearchSeries } from '../../store/series/slice';
import { SearchSeriesPayload } from '../../store/series/types';
import { searchSeries } from '../../store/series/thunks';
import { debounce } from '../../utils/debounce';
import { CalendarEventTypeEnum } from '../../types';
import {
  CalendarEventCreateParams,
  CalendarEventEditParams,
  CreateCalendarEvent,
  EditCalendarEvent,
} from '../CalendarEvents';
import { HaOverviewSubHeader } from './components/HaOverviewSubHeader';
import { FilterSortBar } from './components/FilterSortBar';
import { HaOverviewList } from './components/HaOverviewList';
import { CustomerCard } from './components/CustomerCard';
import { buildPayload } from './lib/buildPayload';
import { countWarnings } from './lib/countWarnings';
import { findCurrentIndexEvent } from './lib/findCurrentIndexEvent';
import { SeriesListParams, SortMode, WarningCounts, WarningFilter } from './types';
import styles from './HaOverviewContainer.module.scss';

const SEARCH_DEBOUNCE_MS = 250;
const REFETCH_DELAY_MS = 2000;

const INITIAL_PARAMS: SeriesListParams = {
  search: '',
  sortMode: 'alpha',
  warnings: [],
  archived: false,
};

const EMPTY_WARNING_COUNTS: WarningCounts = {
  missingOutcome: 0,
  missingFollowup: 0,
  ratioExceeded: 0,
};

export function HaOverviewContainer() {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [listParams, setListParams] = useState<SeriesListParams>(INITIAL_PARAMS);
  const { search, sortMode, archived, warnings } = listParams;
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [editAppointmentData, setEditAppointmentData] = useState<CalendarEventEditParams | undefined>(undefined);
  const [appointmentCreateData, setAppointmentCreateData] = useState<CalendarEventCreateParams | null>(null);

  const searchSeriesState = useAppSelector((state) => state.series.searchSeries);
  const lastCreatedEvent = useAppSelector((state) => state.appointments.createCalendarEvent.data);
  const lastUpdatedEvent = useAppSelector((state) => state.appointments.updateCalendarEvent.data);
  const lastDeletedEventAt = useAppSelector((state) => state.appointments.deleteCalendarEvent.savedAt);
  const lastSeriesOutcomeSavedAt = useAppSelector((state) => state.series.recordSeriesOutcome.savedAt);
  const lastEventOutcomeSavedAt = useAppSelector((state) => state.series.recordEventOutcome.savedAt);
  const lastSeriesUpdatedAt = useAppSelector((state) => state.series.updateSeries.savedAt);
  const lastCustomerUpdatedAt = useAppSelector((state) => state.customers.updateCustomer.savedAt);

  const debouncedDispatch = useMemo(
    () => debounce((payload: SearchSeriesPayload) => {
      dispatch(searchSeries(payload));
    }, SEARCH_DEBOUNCE_MS),
    [dispatch],
  );

  useEffect(() => () => {
    debouncedDispatch.cancel();
    dispatch(resetSearchSeries());
  }, [debouncedDispatch, dispatch]);

  const prevSearchRef = useRef(search);

  useEffect(() => {
    const payload = buildPayload({ search, sortMode, warnings, archived });
    const searchChanged = prevSearchRef.current !== search;
    prevSearchRef.current = search;

    if (searchChanged) {
      debouncedDispatch(payload);
    } else {
      debouncedDispatch.cancel();
      dispatch(searchSeries(payload));
    }
  }, [search, sortMode, warnings, archived, dispatch, debouncedDispatch]);

  // Refetch the series list after the user creates, updates, or deletes a
  // calendar event through one of the modals mounted on this screen, records
  // an outcome on a series / event, or saves the series customer-section
  // (which dispatches updateCustomer / updateSeries independently). We watch
  // the slice's rising-edge markers (`createCalendarEvent.data`,
  // `updateCalendarEvent.data`, `deleteCalendarEvent.savedAt`,
  // `recordSeriesOutcome.savedAt`, `recordEventOutcome.savedAt`,
  // `updateSeries.savedAt`, `updateCustomer.savedAt`) — each flips on
  // `fulfilled`, so a change is a clean signal that survives back-to-back
  // saves. Filters are read through a ref so this effect only depends on
  // those markers.
  //
  // The refetch is deferred to give the BE indexer (RabbitMQ → webhooks
  // → OpenSearch) time to apply the change before the search query runs;
  // otherwise the list still shows stale warnings/dots right after the save.
  const listParamsRef = useRef({ search, sortMode, warnings, archived });
  listParamsRef.current = { search, sortMode, warnings, archived };
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return undefined;
    }

    if (
      lastCreatedEvent === null
      && lastUpdatedEvent === null
      && lastDeletedEventAt === null
      && lastSeriesOutcomeSavedAt === null
      && lastEventOutcomeSavedAt === null
      && lastSeriesUpdatedAt === null
      && lastCustomerUpdatedAt === null
    ) {
      return undefined;
    }

    debouncedDispatch.cancel();
    const timeoutId = setTimeout(() => {
      dispatch(searchSeries(buildPayload(listParamsRef.current)));
    }, REFETCH_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [
    lastCreatedEvent,
    lastUpdatedEvent,
    lastDeletedEventAt,
    lastSeriesOutcomeSavedAt,
    lastEventOutcomeSavedAt,
    lastSeriesUpdatedAt,
    lastCustomerUpdatedAt,
    debouncedDispatch,
    dispatch,
  ]);

  const onSearchChange = useCallback((next: string) => {
    setListParams((current) => ({ ...current, search: next }));
  }, []);

  const onSortModeChange = useCallback((next: SortMode) => {
    setListParams((current) => ({ ...current, sortMode: next }));
  }, []);

  const onWarningChange = useCallback((next: WarningFilter[]) => {
    setListParams((current) => ({ ...current, warnings: next }));
  }, []);

  const onArchivedToggle = useCallback(() => {
    setListParams((current) => ({ ...INITIAL_PARAMS, archived: !current.archived }));
  }, []);

  const onRetry = useCallback(() => {
    debouncedDispatch.cancel();
    dispatch(searchSeries(buildPayload({ search, sortMode, warnings, archived })));
  }, [search, sortMode, warnings, archived, dispatch, debouncedDispatch]);

  const searchData = searchSeriesState.data;

  const onCardClick = useCallback((seriesId: string) => {
    setSelectedSeriesId(seriesId);

    const row = searchData === null ? null : searchData.byId[seriesId];
    if (!row) {
      return;
    }

    const currentEvent = findCurrentIndexEvent(row.events);
    if (!currentEvent) {
      enqueueSnackbar(intl.formatMessage({ id: 'common.defaultError' }), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        preventDuplicate: true,
      });
      return;
    }

    setEditAppointmentData({
      appointmentId: currentEvent.id,
      type: CalendarEventTypeEnum.APPOINTMENT,
      isPastEvent: false,
    });
  }, [searchData, intl]);

  const onEditModalClose = useCallback(() => {
    setEditAppointmentData(undefined);
    setSelectedSeriesId(null);

    // EditCalendarEvent's mount effect (with shouldReload) only refetches when
    // singleAppointment is null; if a stale value is left from a previous open,
    // a second open on a different series renders the wrong event or nothing.
    // Reset it on close so each open starts from a clean fetch.
    dispatch(resetSingleAppointment());
  }, [dispatch]);

  const onEditModalCopyToNew = useCallback((params: CalendarEventCreateParams) => {
    setAppointmentCreateData(params);
  }, []);

  const onCreateModalClose = useCallback(() => {
    setAppointmentCreateData(null);
  }, []);

  const renderRow = useCallback(
    (seriesId: string) => (
      <CustomerCard
        seriesId={seriesId}
        isSelected={seriesId === selectedSeriesId}
        onClick={onCardClick}
      />
    ),
    [selectedSeriesId, onCardClick],
  );

  const { data, error } = searchSeriesState;
  const rowIds = data === null ? [] : data.order;
  const archivedCount = data === null ? 0 : data.archivedCount;

  const warningCounts = useMemo(() => {
    if (data === null) {
      return EMPTY_WARNING_COUNTS;
    }

    return countWarnings(data);
  }, [data]);

  const hasBlockingError = error !== null && data === null;
  const hasToastError = error !== null && data !== null;

  useEffect(() => {
    if (!hasToastError) {
      return;
    }

    enqueueSnackbar(intl.formatMessage({ id: 'common.defaultError' }), {
      variant: 'warning',
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
      preventDuplicate: true,
    });
  }, [error, hasToastError, intl]);

  return (
    <div className={styles.root}>
      <HaOverviewSubHeader search={search} onSearchChange={onSearchChange} />

      <FilterSortBar
        sortMode={sortMode}
        onSortModeChange={onSortModeChange}
        archived={archived}
        archivedCount={archivedCount}
        onArchivedToggle={onArchivedToggle}
        warnings={warnings}
        warningCounts={warningCounts}
        onWarningChange={onWarningChange}
      />

      <HaOverviewList
        rowIds={rowIds}
        loading={searchSeriesState.loading}
        error={hasBlockingError}
        onRetry={onRetry}
        renderRow={renderRow}
      />

      {editAppointmentData && (
        <EditCalendarEvent
          {...editAppointmentData}
          shouldReload
          onClose={onEditModalClose}
          onCopyToNew={onEditModalCopyToNew}
        />
      )}

      {appointmentCreateData && (
        <CreateCalendarEvent onClose={onCreateModalClose} params={appointmentCreateData} />
      )}
    </div>
  );
}
